import { useState } from 'react'
import {
  api,
  ErrorDeApi,
  type CampoBancario,
  type ColumnaDePerfil,
  type DatosDePerfilBancario,
  type PerfilBancario,
} from '../servicios/api'

const SIN_PERMISO = 'Tu rol no puede crear ni editar perfiles de importación: pide a un contador o a un administrador del despacho que lo haga.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

/** Separadores de columnas que acepta el servidor; '' es «que lo detecte en el encabezado». */
export const SEPARADORES: [string, string][] = [
  ['', 'Detectar'],
  [',', 'Coma'],
  [';', 'Punto y coma'],
  ['\t', 'Tabulador'],
  ['|', 'Barra vertical'],
]

const NOMBRE_DE_CAMPO: Record<CampoBancario, string> = {
  Fecha: 'Fecha',
  Concepto: 'Concepto',
  Referencia: 'Referencia',
  Cargo: 'Cargo (retiro)',
  Abono: 'Abono (depósito)',
  ImporteConSigno: 'Importe con signo',
  Saldo: 'Saldo',
}

type ModoDeImporte = 'CargoYAbono' | 'ConSigno'

interface Formulario {
  nombre: string
  separador: string
  hoja: string
  renglonDeEncabezado: string
  formatoDeFecha: string
  separadorDecimal: string
  positivoEsAbono: boolean
  modo: ModoDeImporte
  columnas: Record<CampoBancario, string>
}

const COLUMNAS_VACIAS: Record<CampoBancario, string> = {
  Fecha: '', Concepto: '', Referencia: '', Cargo: '', Abono: '', ImporteConSigno: '', Saldo: '',
}

const NUEVO: Formulario = {
  nombre: '', separador: '', hoja: '', renglonDeEncabezado: '', formatoDeFecha: 'dd/MM/yyyy',
  separadorDecimal: '.', positivoEsAbono: true, modo: 'CargoYAbono', columnas: COLUMNAS_VACIAS,
}

function desdePerfil(p: PerfilBancario): Formulario {
  const columnas = { ...COLUMNAS_VACIAS }
  for (const c of p.columnas) columnas[c.campo] = c.columna
  return {
    nombre: p.nombre,
    separador: p.separador ?? '',
    hoja: p.hoja ?? '',
    renglonDeEncabezado: p.renglonDeEncabezado === null ? '' : String(p.renglonDeEncabezado),
    formatoDeFecha: p.formatoDeFecha,
    separadorDecimal: p.separadorDecimal,
    positivoEsAbono: p.positivoEsAbono,
    modo: p.columnas.some((c) => c.campo === 'ImporteConSigno') ? 'ConSigno' : 'CargoYAbono',
    columnas,
  }
}

/** Solo viajan las columnas del modo de importe elegido y las que se llenaron. */
function aDatos(f: Formulario): DatosDePerfilBancario {
  const campos: CampoBancario[] = f.modo === 'ConSigno'
    ? ['Fecha', 'Concepto', 'Referencia', 'ImporteConSigno', 'Saldo']
    : ['Fecha', 'Concepto', 'Referencia', 'Cargo', 'Abono', 'Saldo']
  const columnas: ColumnaDePerfil[] = campos
    .filter((campo) => f.columnas[campo].trim() !== '')
    .map((campo) => ({ campo, columna: f.columnas[campo].trim() }))
  return {
    nombre: f.nombre.trim(),
    ...(f.separador && { separador: f.separador }),
    ...(f.hoja.trim() && { hoja: f.hoja.trim() }),
    ...(f.renglonDeEncabezado && { renglonDeEncabezado: Number(f.renglonDeEncabezado) }),
    formatoDeFecha: f.formatoDeFecha.trim(),
    separadorDecimal: f.separadorDecimal,
    positivoEsAbono: f.modo === 'ConSigno' ? f.positivoEsAbono : true,
    columnas,
  }
}

const nombreDeSeparador = (s: string | null) => SEPARADORES.find(([valor]) => valor === (s ?? ''))?.[1] ?? s

/**
 * Perfiles de importación de estados de cuenta (RF-1301). Son del despacho y se reusan entre sus
 * clientes: un banco nuevo, o uno que cambió su formato sin avisar, se atiende aquí editando el
 * perfil, no esperando un despliegue.
 */
export function PerfilesBancarios({
  perfiles,
  puedeEditar,
  alGuardar,
}: {
  perfiles: PerfilBancario[] | undefined
  puedeEditar: boolean
  alGuardar: () => Promise<void>
}) {
  // undefined: sin formulario abierto; null: perfil nuevo; un id: editando ese perfil.
  const [editando, setEditando] = useState<string | null | undefined>(undefined)
  const [formulario, setFormulario] = useState<Formulario>(NUEVO)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  function abrir(perfil?: PerfilBancario) {
    setEditando(perfil ? perfil.id : null)
    setFormulario(perfil ? desdePerfil(perfil) : NUEVO)
    setError('')
  }

  function cambiar<K extends keyof Formulario>(campo: K, valor: Formulario[K]) {
    setFormulario((f) => ({ ...f, [campo]: valor }))
  }

  function cambiarColumna(campo: CampoBancario, valor: string) {
    setFormulario((f) => ({ ...f, columnas: { ...f.columnas, [campo]: valor } }))
  }

  async function guardar() {
    setOcupado(true)
    setError('')
    try {
      const datos = aDatos(formulario)
      if (editando) await api.actualizarPerfilBancario(editando, datos)
      else await api.crearPerfilBancario(datos)
      setEditando(undefined)
      await alGuardar()
    } catch (e) {
      setError(mensaje(e, 'No se pudo guardar el perfil.'))
    } finally {
      setOcupado(false)
    }
  }

  const campoDeColumna = (campo: CampoBancario, requerido: boolean) => (
    <label key={campo}>
      {NOMBRE_DE_CAMPO[campo]}{requerido ? '' : ' (opcional)'}
      <input
        value={formulario.columnas[campo]}
        onChange={(e) => cambiarColumna(campo, e.target.value)}
        required={requerido}
        placeholder="Título o #3"
      />
    </label>
  )

  return (
    <section aria-labelledby="titulo-perfiles">
      <header className="encabezado">
        <h2 id="titulo-perfiles">Perfiles de importación</h2>
        {puedeEditar && editando === undefined && (
          <span className="encabezado-acciones">
            <button className="secundario" onClick={() => abrir()}>Nuevo perfil</button>
          </span>
        )}
      </header>

      <p className="nota">
        Un perfil dice cómo leer el estado de cuenta de un banco. Es del despacho: sirve para todos los clientes que
        usan ese banco.
      </p>

      {perfiles && perfiles.length === 0 && (
        <p className="vacio">
          El despacho aún no tiene perfiles de importación.
          {puedeEditar ? ' Crea el primero con «Nuevo perfil».' : ' Pide a un contador o a un administrador que cree el del banco.'}
        </p>
      )}

      {perfiles && perfiles.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Perfil</th>
                <th scope="col">Separador</th>
                <th scope="col">Fecha</th>
                <th scope="col">Columnas</th>
                <th scope="col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {perfiles.map((p) => (
                <tr key={p.id}>
                  <th scope="row">{p.nombre}</th>
                  <td>{nombreDeSeparador(p.separador)} · decimal «{p.separadorDecimal}»</td>
                  <td className="mono">{p.formatoDeFecha}</td>
                  <td className="sutil">{p.columnas.map((c) => `${NOMBRE_DE_CAMPO[c.campo]}: ${c.columna}`).join(' · ')}</td>
                  <td>
                    {puedeEditar && (
                      <button className="enlace" onClick={() => abrir(p)} aria-label={`Editar ${p.nombre}`}>Editar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editando !== undefined && (
        <form
          className="tarjeta"
          aria-label={editando ? 'Editar perfil de importación' : 'Nuevo perfil de importación'}
          onSubmit={(e) => {
            e.preventDefault()
            void guardar()
          }}
        >
          <h3>{editando ? 'Editar perfil' : 'Nuevo perfil'}</h3>

          <label>
            Nombre
            <input value={formulario.nombre} onChange={(e) => cambiar('nombre', e.target.value)} maxLength={100} required
              placeholder="BBVA · CSV de banca en línea" />
          </label>

          <div className="filtros">
            <label>
              Separador de columnas (CSV)
              <select value={formulario.separador} onChange={(e) => cambiar('separador', e.target.value)}>
                {SEPARADORES.map(([valor, etiqueta]) => <option key={etiqueta} value={valor}>{etiqueta}</option>)}
              </select>
            </label>
            <label>
              Hoja (XLSX, opcional)
              <input value={formulario.hoja} onChange={(e) => cambiar('hoja', e.target.value)} maxLength={100} placeholder="La primera" />
            </label>
            <label>
              Renglón del encabezado (opcional)
              <input type="number" min="1" step="1" value={formulario.renglonDeEncabezado}
                onChange={(e) => cambiar('renglonDeEncabezado', e.target.value)} placeholder="Detectar" />
            </label>
            <label>
              Formato de fecha
              <input value={formulario.formatoDeFecha} onChange={(e) => cambiar('formatoDeFecha', e.target.value)} maxLength={40}
                required className="mono" />
            </label>
            <label>
              Separador decimal
              <select value={formulario.separadorDecimal} onChange={(e) => cambiar('separadorDecimal', e.target.value)}>
                <option value=".">Punto (1,234.56)</option>
                <option value=",">Coma (1.234,56)</option>
              </select>
            </label>
          </div>

          <fieldset>
            <legend>¿Cómo trae el banco el importe?</legend>
            <label className="casilla">
              <input type="radio" name="modo" checked={formulario.modo === 'CargoYAbono'}
                onChange={() => cambiar('modo', 'CargoYAbono')} />
              En dos columnas: cargo y abono
            </label>
            <label className="casilla">
              <input type="radio" name="modo" checked={formulario.modo === 'ConSigno'}
                onChange={() => cambiar('modo', 'ConSigno')} />
              En una columna con signo
            </label>
          </fieldset>

          <div className="filtros">
            {campoDeColumna('Fecha', true)}
            {campoDeColumna('Concepto', true)}
            {campoDeColumna('Referencia', false)}
            {formulario.modo === 'CargoYAbono'
              ? [campoDeColumna('Cargo', true), campoDeColumna('Abono', true)]
              : campoDeColumna('ImporteConSigno', true)}
            {campoDeColumna('Saldo', false)}
          </div>

          {formulario.modo === 'ConSigno' && (
            <label className="casilla">
              <input type="checkbox" checked={formulario.positivoEsAbono}
                onChange={(e) => cambiar('positivoEsAbono', e.target.checked)} />
              El importe positivo es un depósito (abono)
            </label>
          )}

          <p className="nota">
            Escribe cada columna como aparece en el encabezado del banco (no importan mayúsculas ni acentos) o por su
            posición, como #3. Si usas posiciones, indica el renglón del encabezado. La coma no puede separar columnas y
            decimales a la vez: con decimales con coma, el CSV suele venir con punto y coma.
          </p>

          {error && <p className="error" role="alert">{error}</p>}

          <div className="acciones">
            <button type="button" onClick={() => setEditando(undefined)}>Cancelar</button>
            <button type="submit" className="primario" disabled={ocupado}>{ocupado ? 'Guardando…' : 'Guardar perfil'}</button>
          </div>
        </form>
      )}
    </section>
  )
}
